// ---------------------------------------------------------------------------
// Authored by Claude (Anthropic), working in Cowork with Matt Olden,
// Context Networks. Written 2026-10-29.
// Decisions, conventions and production data corrections are Matt's;
// the code is Claude's.
// ---------------------------------------------------------------------------
import React, { useState, useEffect, useRef } from 'react';
import { Building2, X, Search } from 'lucide-react';
import { useAuth } from '../auth/AuthContext';

const VenueSelectionModal = () => {
  const { user, selectVenue, inProgress } = useAuth();
  const selectingVenue = inProgress === 'SelectingVenue';
  const [showModal, setShowModal] = useState(false);
  const [venueNames, setVenueNames] = useState({});
  const [loadingVenues, setLoadingVenues] = useState(false);
  const dismissedRef = useRef(false);
  const [query, setQuery] = useState('');
  // The search box only appears for users with more venues than this (short lists do not need it)
  const SEARCH_MIN_VENUES = 6;

  // Start each time the modal opens with an empty search
  useEffect(() => {
    if (showModal) setQuery('');
  }, [showModal]);

  // Show modal when user has multiple venues but no current venue selected (unless user dismissed or selecting)
  useEffect(() => {
    if (user?.venueUid) {
      dismissedRef.current = false; // Reset when venue is set (e.g. from navbar selector)
    }
    if (user?.hasMultipleVenues && !user?.venueUid && !dismissedRef.current && !selectingVenue) {
      setShowModal(true);
    } else {
      setShowModal(false);
    }
  }, [user, selectingVenue]);

  // Fetch venue names for display
  useEffect(() => {
    const fetchVenueNames = async () => {
      if (!user?.venues?.length) return;
      
      setLoadingVenues(true);
      try {
        // Use the my-venues endpoint to get only user's associated venues with display names
        const response = await fetch(`/api/venues/my-venues`, {
          credentials: 'include'
        });
        
        if (response.ok) {
          const venues = await response.json();
          const nameMap = {};
          const userVenueUids = new Set(
            user.venues.map(v => v.venue_uid).filter(Boolean)
          ); // Create set for fast lookup
          
          // Only include venues that the user actually has access to
          venues.forEach(venue => {
            if (userVenueUids.has(venue.uid)) {
              nameMap[venue.uid] = `${venue.publisherName} — ${venue.venueName}`;
            }
          });
          setVenueNames(nameMap);
        }
      } catch (error) {
        console.error('Failed to fetch venue names:', error);
      } finally {
        setLoadingVenues(false);
      }
    };

    if (showModal) {
      fetchVenueNames();
    }
  }, [user?.venues, showModal]);

  const handleVenueSelect = async (venueUid) => {
    dismissedRef.current = true; // Prevent useEffect from reopening during async
    setShowModal(false);
    await selectVenue(venueUid);
  };

  if (!showModal) {
    return null;
  }

  const handleClose = () => { dismissedRef.current = true; setShowModal(false); };

  // Venues shown in the list: sorted by display name, narrowed by what the user typed
  // (matches the venue name, publisher name or venue ID, case-insensitive).
  const venueLabel = (uid) => venueNames[uid] || uid;
  const q = query.trim().toLowerCase();
  const visibleVenues = [...(user.venues || [])]
    .sort((a, b) => String(venueLabel(a.venue_uid)).localeCompare(String(venueLabel(b.venue_uid)), undefined, { sensitivity: 'base' }))
    .filter((v) => !q || `${venueLabel(v.venue_uid)} ${v.venue_uid}`.toLowerCase().includes(q));
  const showSearch = (user.venues || []).length > SEARCH_MIN_VENUES;

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
      onClick={handleClose}
    >
      <div
        className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full mx-4 max-h-[85vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6 flex-shrink-0">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white flex items-center space-x-2">
              <Building2 className="w-6 h-6 text-blue-600" />
              <span>Select Your Venue</span>
            </h2>
            <button
              onClick={handleClose}
              className="p-1.5 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              title="Close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          
          <p className="text-gray-600 dark:text-gray-300 mb-4">
            You have access to multiple venues. Please select which venue you'd like to manage in this session.
          </p>

          {loadingVenues ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <span className="ml-2 text-gray-600 dark:text-gray-300">Loading venues...</span>
            </div>
          ) : (
            <>
            {showSearch && (
              <div className="relative mb-3">
                <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                <input
                  type="text"
                  autoFocus
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && visibleVenues.length === 1 && !selectingVenue) {
                      handleVenueSelect(visibleVenues[0].venue_uid);
                    }
                  }}
                  placeholder="Search venues by name or ID"
                  aria-label="Search venues"
                  className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Showing {visibleVenues.length} of {user.venues.length} venues
                </div>
              </div>
            )}
            <div className="space-y-2 overflow-y-auto max-h-[45vh] min-h-[120px] pr-1 -mr-1">
              {visibleVenues.length === 0 && (
                <div className="py-6 text-center text-sm text-gray-500 dark:text-gray-400">
                  No venues match "{query}"
                </div>
              )}
              {visibleVenues.map((venue) => (
                <button
                  key={venue.venue_uid}
                  onClick={() => handleVenueSelect(venue.venue_uid)}
                  disabled={selectingVenue}
                  className="w-full text-left p-4 border border-gray-200 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50 transition-colors duration-200"
                >
                  <div className="flex items-center space-x-3">
                    <Building2 className="w-5 h-5 text-gray-400 flex-shrink-0" />
                    <div className="min-w-0">
                      <div className="font-medium text-gray-900 dark:text-white truncate">
                        {venueNames[venue.venue_uid] || venue.venue_uid}
                      </div>
                      <div className="text-sm text-gray-500 dark:text-gray-400">
                        Venue ID: {venue.venue_uid}
                      </div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
            </>
          )}

          <div className="mt-4 text-xs text-gray-500 dark:text-gray-400">
            You can change the venue later from the first step of starting a new campaign.
          </div>
        </div>
      </div>
    </div>
  );
};

export default VenueSelectionModal;